import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  fetchCollectionAllocations,
  fetchCollectionCashEvents,
} from '#/api/crmMgmt/customerOrder/collection';
import { PERM } from '#/config/permissions';
import { Button } from '#/modules/commonComponents/button';
import {
  COLLECTION_CASH_EVENT_LABEL,
  canReverseCollection,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { useCan } from '#/modules/infra/auth/useCan';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import { CollectionRefundDialog, CollectionRefundVoidDialog } from './CollectionRefundDialogs';
import CollectionSimpleTable from './CollectionSimpleTable';
import CollectionStatusBadge from './CollectionStatusBadge';
import { collectionQueryKey } from './collectionQueryKeys';

const eventTypeOf = (row) => row.eventType ?? row.type;

/** 收款沖銷／退款／退款沖銷事件（不可覆寫，原收款保留）。 */
const CollectionCashEventsCard = ({ detail, receipts, summary }) => {
  const customerOrderId = detail.id;
  const canConfirm = useCan(PERM.FMS_CUSTOMER_COLLECTION_CONFIRM);
  const reversible = canConfirm && canReverseCollection(detail.status);
  const [dialog, setDialog] = useState(null);

  const eventsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'cashEvents'),
    queryFn: () => fetchCollectionCashEvents(customerOrderId),
  });
  const allocationsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'allocations'),
    queryFn: () => fetchCollectionAllocations(customerOrderId),
  });
  const events = useMemo(() => eventsQuery.data?.data ?? [], [eventsQuery.data]);

  const columns = useMemo(() => {
    const voidedRefundIds = new Set(
      events.filter((row) => eventTypeOf(row) === 'REFUND_VOID').map((row) => row.parentEventId),
    );
    return [
      {
        id: 'eventType',
        header: '類型',
        accessorFn: eventTypeOf,
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <CollectionStatusBadge status={eventTypeOf(row.original)} labels={COLLECTION_CASH_EVENT_LABEL} />
        ),
        meta: { statusPresentation: 'badge' },
      },
      {
        id: 'amount',
        header: '金額',
        accessorFn: (row) => formatDisplayAmount(row.amount),
        size: 110,
        enableSorting: false,
        meta: { cellClassName: 'td_right' },
      },
      {
        id: 'actualOccurredAt',
        header: '發生時間',
        accessorFn: (row) => formatDateTimeOrDash(row.actualOccurredAt),
        size: 150,
        enableSorting: false,
      },
      {
        id: 'reason',
        header: '原因',
        accessorFn: (row) => row.reason || '—',
        size: 200,
        enableSorting: false,
        meta: { cellClassName: 'td_left' },
      },
      {
        id: 'actions',
        header: '操作',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          reversible && eventTypeOf(row.original) === 'REFUND' && !voidedRefundIds.has(row.original.id) ? (
            <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'void', refund: row.original })}>
              沖銷退款
            </Button>
          ) : null
        ),
      },
    ];
  }, [events, reversible]);

  const closeDialog = (nextOpen) => {
    if (!nextOpen) {
      setDialog(null);
    }
  };
  const hasConfirmed = receipts.some((receipt) => receipt.status === 'CONFIRMED');

  return (
    <div className="space-y-2">
      {reversible && hasConfirmed ? (
        <div className="flex justify-end px-4 pt-3">
          <Button type="button" size="sm" onClick={() => setDialog({ type: 'refund' })}>
            登錄退款
          </Button>
        </div>
      ) : null}
      <CollectionSimpleTable
        rows={events}
        columns={columns}
        emptyText="尚無退款或沖銷"
        rowIdPrefix="cash-event"
      />
      {dialog?.type === 'refund' ? (
        <CollectionRefundDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          receipts={receipts}
          allocations={allocationsQuery.data?.data ?? []}
          summaryPhases={summary?.phases ?? []}
          salesOrders={detail.salesOrders ?? []}
        />
      ) : null}
      {dialog?.type === 'void' ? (
        <CollectionRefundVoidDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          refund={dialog.refund}
        />
      ) : null}
    </div>
  );
};

CollectionCashEventsCard.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    salesOrders: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  receipts: PropTypes.arrayOf(PropTypes.object).isRequired,
  summary: PropTypes.object,
};

export default CollectionCashEventsCard;
